import { Box, Center, Flex, Heading, Spacer, Stack, type StyleProps, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import useIsVisible from '@/client/hooks/useIsVisible'
import RPC from '@/client/rpc'
import composeRichText from '@/client/services/richText/composeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'

import ConversationTypingStatus from '@/components/Conversation/ConversationTypingStatus'
import Message from '@/components/Conversation/Message'
import NewMessage from '@/components/Conversation/NewMessage'
import Spinner from '@/components/Spinner'
import QuickReplyContext from '@/components/UI/RichTextEditor/QuickReplyContext'
import type { FileUpload } from '@/components/Worlds/FileUpload/types'
import useUpdateConversationLastReadAt from '@/components/Worlds/hooks/user/useUpdateConversationLastReadAt'

import ConversationHeader from './ConversationHeader'
import { ConversationSingleLoading } from './ConversationLoading'
import ConversationUsersSeen from './ConversationUsersSeen'
import { EMPTY_MESSAGE_ID } from './constants'
import useRealtimeConversation from './hooks/useRealtimeConversation'

export default function ConversationSingle({
  conversationUuid,
  hideHeader = false,
  height,
  isAdminView = false,
  headerVariant,
  onMinimize,
  onBack,
  hideBackButton,
}: {
  conversationUuid: string
  hideHeader?: boolean
  height?: StyleProps['height']
  isAdminView?: boolean
  headerVariant?: 'default' | 'floating'
  onMinimize?: () => void
  onBack?: () => void
  hideBackButton?: boolean
}): React.ReactElement {
  const currentUser = useUser()
  const router = useRouter()

  const messageListRef = useRef<HTMLDivElement>(null)
  const messageListScrollAnchor = useRef<string | null>(null)
  const [latestMessageCreatedAt, setLatestMessageCreatedAt] = useState<Date | null>(null)

  const { loading, messages, conversation, hasMore, error, onLoadMore } = useRealtimeConversation({
    conversationUuid,
  })
  const firstMessageClientId = messages?.[0]?.client_id ?? null

  useEffect(() => {
    if (error != null) {
      logger.error('Error loading conversation', { error, conversationUuid })
    }
  }, [error, conversationUuid])

  const [scrollRef] = useIsVisible<HTMLDivElement>({
    onVisible: useCallback(() => {
      if (hasMore && !loading && latestMessageCreatedAt != null) {
        messageListScrollAnchor.current = firstMessageClientId
        onLoadMore()
      }
    }, [hasMore, loading, latestMessageCreatedAt, firstMessageClientId, onLoadMore]),
  })

  const messageListAnchorRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element == null || loading) {
        return
      }
      element.scrollIntoView()

      const currentMessageId = messageListScrollAnchor.current

      // Number chosen arbitrarily to ensure the scroll is complete
      // React mounting and remounting components can cause the scroll to be off
      setTimeout(() => {
        if (currentMessageId === messageListScrollAnchor.current) {
          messageListScrollAnchor.current = null
        }
      }, 2500)
    },
    [loading]
  )

  const [optimisticMessages, setOptimisticMessages] = useState<
    NonNullable<typeof messages>[number][]
  >([])

  const scrollToBottom = useCallback(() => {
    if (messageListRef.current == null) {
      return
    }
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight
  }, [])

  useEffect(() => {
    setLatestMessageCreatedAt(null)
    setOptimisticMessages([])
  }, [conversationUuid])

  // Scroll to bottom in case of new self-sent messages
  useEffect(() => {
    scrollToBottom()
  }, [optimisticMessages.length, scrollToBottom])

  // Scroll to bottom in case of new incoming messages
  useEffect(() => {
    if (messages == null || messages.length === 0 || messageListRef.current == null) {
      return
    }
    const lastMessageCreatedAt = new Date(messages[messages.length - 1].created_at)
    if (latestMessageCreatedAt == null || lastMessageCreatedAt > latestMessageCreatedAt) {
      setLatestMessageCreatedAt(lastMessageCreatedAt)
      scrollToBottom()
    }
  }, [messages, latestMessageCreatedAt, scrollToBottom])

  useUpdateConversationLastReadAt({ conversationId: conversation?.id, latestMessageCreatedAt })

  const onMessageSubmit = useCallback(
    ({
      body: newBody,
      clientId,
      attachments,
    }: {
      body: SerializedRichText
      clientId: string
      attachments: FileUpload[]
    }) => {
      setOptimisticMessages(oldMessages => [
        ...oldMessages,
        {
          id: EMPTY_MESSAGE_ID,
          client_id: clientId,
          type: 'plain-v1',
          body_safe: newBody,
          attachment_urls_safe: attachments.map(attachment => attachment.src),
          user: {
            id: currentUser.id,
            virtual_display_name: currentUser.displayName,
            virtual_avatar_url: currentUser.avatarUrl,
            virtual_progression_points: 0,
          },
          user_id: currentUser.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          message_reactions: [],
          last_edited_at: null,
          hidden_at: null,
        },
      ])
      scrollToBottom()
    },
    [currentUser, scrollToBottom]
  )

  const onMessageSubmitFailed = useCallback((clientId: string) => {
    setOptimisticMessages(oldMessages =>
      oldMessages.filter(oldMessage => oldMessage.client_id !== clientId)
    )
  }, [])

  const [handleQuickReply] = useAPIWrapper(
    async (replyText: string) => {
      if (conversation == null) {
        return
      }
      const clientId = uuidv4()
      const body = composeRichText(ctx => ctx.text(replyText))
      onMessageSubmit({ body, clientId, attachments: [] })

      const result = await RPC.sendMessage(
        { conversationId: conversation.id, body, attachmentUrls: [], clientId },
        { disableProgressBar: true }
      )

      if (result.status === 'blocked') {
        onMessageSubmitFailed(clientId)
        return
      }

      if (result.status === 'maybe-suspended') {
        router.reload()
      }
    },
    [conversation, onMessageSubmit, onMessageSubmitFailed, router],
    'Send quick reply'
  )

  const quickReplyContext = useMemo(
    () => (conversation != null ? { onQuickReply: handleQuickReply } : null),
    [conversation, handleQuickReply]
  )

  if (conversation == null || messages == null) {
    return <ConversationSingleLoading />
  }

  const messagesToRender = messages.concat(
    optimisticMessages.filter(
      optimisticMessage =>
        !messages.some(message => message.client_id === optimisticMessage.client_id)
    )
  )

  return (
    <Flex direction="column" height={height} maxHeight={height} position="static">
      {hideHeader === true ? null : (
        <ConversationHeader
          conversation={conversation}
          isAdminView={isAdminView}
          variant={headerVariant}
          onMinimize={onMinimize}
          onBack={onBack}
          hideBackButton={hideBackButton}
        />
      )}
      <QuickReplyContext.Provider value={quickReplyContext}>
        <Flex direction="column" gap="2px" overflowY="auto" p="24px" ref={messageListRef}>
          {!loading && messages.length === 0 ? (
            <Center p="24px">
              <Stack direction="column" spacing="24px" align="center">
                <Heading size="xl" as="h1">
                  No messages yet!
                </Heading>
                <Text>
                  You don&apos;t have any messages yet. Once you do, they&apos;ll go here!
                </Text>
              </Stack>
            </Center>
          ) : (
            <>
              <Flex justify="center" width="100%" ref={scrollRef}>
                {/* Only show spinner when loading for more items, not during initial loading */}
                {loading && hasMore ? <Spinner size="md" /> : null}
              </Flex>

              {messagesToRender.map((message, index) => {
                const seenByUsers = conversation.conversation_users.filter(conversationUser => {
                  const lastReadAt = conversationUser.last_read_at
                  if (
                    message.id === 0 ||
                    lastReadAt == null ||
                    conversationUser.user.id === currentUser.id
                  ) {
                    return false
                  }

                  return (
                    new Date(lastReadAt) >= new Date(message.created_at) &&
                    (index === messages.length - 1 ||
                      new Date(lastReadAt) < new Date(messages[index + 1].created_at))
                  )
                })

                return (
                  <Box
                    key={`msg-${message.id}-${message.created_at}`}
                    ref={
                      message.client_id === messageListScrollAnchor.current
                        ? messageListAnchorRef
                        : null
                    }
                  >
                    <Message
                      message={message}
                      lastMessage={messages[index - 1] ?? null}
                      isAdminView={isAdminView}
                    />
                    <ConversationUsersSeen
                      conversationUsers={seenByUsers}
                      indexFromBottom={messages.length - index}
                    />
                  </Box>
                )
              })}
            </>
          )}
        </Flex>
      </QuickReplyContext.Provider>
      <Spacer />
      <Box>
        <ConversationTypingStatus conversation={conversation} messages={messages} />
        <NewMessage
          conversation={conversation}
          onSubmit={onMessageSubmit}
          onSubmitFailed={onMessageSubmitFailed}
          isAdminView={isAdminView}
        />
      </Box>
    </Flex>
  )
}
